import type { TimestampInMillis } from '@cobliteam/common-types';
import moment from 'moment';
import type { IdentificationTokenV2 as IdentificationTokenV2GraphQL } from '../../__generated__/graphql';
import type { Driver, DriverGraphQLAPI } from '../driver';
import type { AssociationsAPI, DriverToTokenAssociationAPI, DriverToTokenAssociationV2API, IdentificationTokenAPI, TokenValidationAPI, TokenValidationV2API } from './APITypes';
import type {
  Associations,
  AssociationsV2,
  DriverToTokenAssociation,
  DriverToTokenAssociationV2,
  IdentificationToken,
  IdentificationTokenV2,
  TokenValidation,
  TokenValidationV2,
} from './Types';
import { IdentificationTokenType } from './Types';

const millisToDateString = (millis?: TimestampInMillis): string | undefined => {
  return (millis && moment(millis).format('YYYY-MM-DD')) || undefined;
};

const parseTokenType = (type?: string): IdentificationTokenType => {
  if (!type) return IdentificationTokenType.UNKNOWN;
  const normalized = type.toLowerCase();
  if (normalized === 'ibutton') return IdentificationTokenType.IBUTTON;
  if (normalized === 'rfid') return IdentificationTokenType.RFID;
  return IdentificationTokenType.UNKNOWN;
};

export const identificationTokenGraphQLV2ToIdentificationTokenV2 = (graphQL: IdentificationTokenV2GraphQL): IdentificationTokenV2 => ({
  id: graphQL.id,
  alias: graphQL.alias,
  tokenId: graphQL.tokenId ?? graphQL.id,
  tokenAlias: graphQL.alias,
  tokenType: parseTokenType(graphQL.type),
  cobliId: graphQL.cobliId ?? undefined,
  status: graphQL.status,
  isExternal: graphQL.isExternal,
  fleetId: graphQL.fleetId,
  driverId: graphQL.driverId ?? undefined,
  associatedToFleetAt: graphQL.associatedToFleetAt ?? undefined,
  associatedToDriverAt: graphQL.associatedToDriverAt ?? undefined,
});

export const identificationTokenGraphQLToIdentificationToken = (graphQLIdentificationToken: IdentificationTokenAPI): IdentificationToken => {
  return {
    tokenId: graphQLIdentificationToken.token_id,
    tokenType: graphQLIdentificationToken.token_type,
    status: graphQLIdentificationToken.status,
    cobliInternalId: graphQLIdentificationToken.cobli_internal_id,
    fleetId: graphQLIdentificationToken.fleet_id,
    driverId: graphQLIdentificationToken.driver_id,
    associatedToFleetAt: graphQLIdentificationToken.associated_to_fleet_at,
  };
};

export const driverToTokenAssociationtransformer = (driverToTokenAssociation: DriverToTokenAssociationAPI): DriverToTokenAssociation => ({
  driverId: driverToTokenAssociation.driver_id,
  cobliInternalId: driverToTokenAssociation.cobli_internal_id?.toUpperCase(),
});

export const driverToTokenAssociationV2transformer = (driverToTokenAssociation: DriverToTokenAssociationV2API): DriverToTokenAssociationV2 => ({
  driverId: driverToTokenAssociation.driver_id,
  tokenAlias: driverToTokenAssociation.alias,
});

export const associationsTransformer = (associations: AssociationsAPI): Associations => ({
  associations: associations.associations.map(driverToTokenAssociationtransformer),
});

export const associationsV2Transformer = (associations: DriverToTokenAssociationV2API[]): AssociationsV2 => ({
  associations: associations.map(driverToTokenAssociationV2transformer),
});

export const driverToDriverGraphQLAPI = (driver: Driver): DriverGraphQLAPI => {
  const { fleetId, id, name, assigned, phoneNumbers, firstLicenseAcquirementDateInMillis, licenseExpirationDateInMillis, licenseCategory, driverCode } = driver;

  return {
    id,
    name,
    fleet_id: fleetId,
    assigned: assigned === true,
    phone_number: phoneNumbers[0],
    first_license_acquirement_date: millisToDateString(firstLicenseAcquirementDateInMillis),
    license_expiration_date: millisToDateString(licenseExpirationDateInMillis),
    license_category: licenseCategory,
    driver_code: driverCode,
  };
};

export const tokenValidationTransformer = (tokenValidationData: TokenValidationAPI): TokenValidation => ({
  tokenId: tokenValidationData.token_id,
  cobliInternalId: tokenValidationData.cobli_internal_id,
  fleetId: tokenValidationData.fleet_id,
  driver: tokenValidationData.driver,
});

export const tokenValidationV2Transformer = (tokenValidationData: TokenValidationV2API): TokenValidationV2 => ({
  tokenId: tokenValidationData.token_id,
  tokenAlias: tokenValidationData.token_alias,
  isExternal: tokenValidationData.is_external,
  driver: tokenValidationData.driver,
});
